/**
 * Browser storage access can throw when storage is blocked (including some embeds).
 * Keep values available for the lifetime of this page when persistence is unavailable.
 */
export enum StorageKey {
  AuthToken = 'authToken',
  Email = 'email',
  /** Legacy cross-tab mirror of session org ID. Do not read for identity. */
  OrgId = 'orgId',
  OrgName = 'orgName',
  /** Legacy cross-tab mirror of session user ID. Do not read for identity. */
  UserId = 'userId',
  FirstName = 'firstName',
  LastName = 'lastName',
  RouteAfterRedirect = 'routeAfterRedirect',
  SessionId = 'sessionId',
  DismissNavigationShortcut = 'dismiss_navigation_shortcut',
  CalEventTypeId = 'calEventTypeID',
  CalApiKey = 'calAPIKey',
  LastTestPhoneNumber = 'lastTestPhoneNumber',
  LastTestPhoneName = 'lastTestPhoneName',
  DemoUserEmail = 'demoUserEmail',
  UseLocalLiveKitServer = 'useLocalLiveKitServer',
  UseLocalLiveKitAgent = 'useLocalLiveKitAgent',
  TestCallTtsSpeakerTag = 'testCallTtsSpeakerTag',
  TestCallTtsV4Stability = 'testCallTtsV4Stability',
  TestCallTtsV4Similarity = 'testCallTtsV4Similarity',
  TestCallTtsV4CloneStrength = 'testCallTtsV4CloneStrength',
  /** Recruiter UI language, cached so the next load can render in it before auth. */
  UiLanguage = 'uiLanguage',
  /**
   * The last verified session (see stores/sessionCache), so the next load can
   * render protected pages before verification returns.
   */
  CachedSession = 'cachedSession',
}

/** sessionStorage keys: scoped to this tab, kept across reloads. */
export enum SessionStorageKey {
  /** Browser proof and state for a single Microsoft support login round trip. */
  SupportMicrosoftBrowserBinding = 'supportMicrosoftBrowserBinding',
  SupportMicrosoftState = 'supportMicrosoftState',
  /** Support-only display override; never changes a member's saved preference. */
  SupportUiLanguage = 'supportUiLanguage',
  /** Email typed before an SSO redirect, sent back when the redirect returns. */
  SsoEnteredEmail = 'pending_sso_entered_email',
  /** Which email integration an OAuth consent redirect is connecting. */
  EmailOAuthPurpose = 'emailOAuthPurpose',
  /** Set before a chunk-error reload so a persistent failure reloads only once. */
  ChunkReloadAttempted = 'salv:chunk-reload-attempted',
  InFlightInterviewMarker = 'inFlightInterviewMarker',
  RescheduleDraft = 'rescheduleDraft',
  BullhornSidebarHidden = 'sidebarAlwaysHidden',
  ZoomPanel = 'tenzo.zoomPanel',
  /** Global generated-script slot from older clients; only ever removed. */
  LegacyGeneratedScript = 'generatedScriptData',
}

interface TypedStorage<K extends string> {
  get(key: K): string | null;
  set(key: K, value: string): void;
  remove(key: K): void;
  /** For keys built at runtime (per job, per call). */
  getDynamic(key: string): string | null;
  setDynamic(key: string, value: string): void;
  removeDynamic(key: string): void;
  keys(): string[];
  clear(): void;
  /** Writes only to browser storage; false when the value will not survive a reload. */
  setPersisted(key: string, value: string): boolean;
  /** Reads browser storage directly, bypassing page fallbacks; throws if unreadable. */
  getPersisted(key: string): string | null;
  /** Removes only from browser storage; false when that removal failed. */
  removePersisted(key: string): boolean;
  clearExcept(keep: K): boolean;
}

function createTypedStorage<K extends string>(
  getStorage: () => Storage
): TypedStorage<K> {
  const fallback = new Map<string, string | null>();
  let ignorePersistent = false;

  function browserStorage(): Storage | null {
    try {
      return getStorage();
    } catch {
      // swallow-ok: Blocked browser storage is represented by null; set/remove use the page-memory fallback and clear hides inaccessible persisted values.
      return null;
    }
  }

  function get(key: string): string | null {
    if (fallback.has(key)) return fallback.get(key) ?? null;
    if (ignorePersistent) return null;
    try {
      return browserStorage()?.getItem(key) ?? null;
    } catch {
      // swallow-ok: An unreadable browser key is absent; page-memory values and deletion tombstones take priority, while absent auth tokens remain signed out.
      return null;
    }
  }

  function set(key: string, value: string): void {
    try {
      const persistent = browserStorage();
      if (!persistent) {
        fallback.set(key, value);
        return;
      }
      persistent.setItem(key, value);
      if (ignorePersistent) fallback.set(key, value);
      else fallback.delete(key);
    } catch {
      fallback.set(key, value);
    }
  }

  function remove(key: string): void {
    try {
      const persistent = browserStorage();
      if (!persistent) {
        fallback.set(key, null);
        return;
      }
      persistent.removeItem(key);
      if (ignorePersistent) fallback.set(key, null);
      else fallback.delete(key);
    } catch {
      fallback.set(key, null);
    }
  }

  function keys(): string[] {
    const result = new Set(
      [...fallback].filter(([, value]) => value !== null).map(([key]) => key)
    );
    if (ignorePersistent) return [...result];
    try {
      const persistent = browserStorage();
      if (persistent) {
        for (let index = 0; index < persistent.length; index++) {
          const key = persistent.key(index);
          if (key !== null && fallback.get(key) !== null) result.add(key);
        }
      }
    } catch {
      // swallow-ok: Inaccessible browser enumeration retains the memory keys already collected; it never resurrects keys hidden by clear or deletion tombstones.
      // Use only the in-memory keys.
    }
    return [...result];
  }

  return {
    get,
    set,
    remove,
    getDynamic: get,
    setDynamic: set,
    removeDynamic: remove,
    keys,
    clear(): void {
      fallback.clear();
      try {
        const persistent = browserStorage();
        if (!persistent) {
          ignorePersistent = true;
          return;
        }
        persistent.clear();
        ignorePersistent = false;
      } catch {
        // Hide any previously persisted values for the rest of this page.
        ignorePersistent = true;
      }
    },
    setPersisted(key: string, value: string): boolean {
      try {
        const persistent = browserStorage();
        if (!persistent) return false;
        persistent.setItem(key, value);
        fallback.delete(key);
        return true;
      } catch {
        // swallow-ok: False explicitly reports that persistence failed; reload-critical callers gate navigation/cache reliance and optional mirrors do not determine operation success.
        return false;
      }
    },
    getPersisted(key: string): string | null {
      // Unlike get(), reload-critical handshakes must distinguish an absent key
      // from an unreadable one before replacing any one-time retry authority.
      return getStorage().getItem(key);
    },
    removePersisted(key: string): boolean {
      try {
        const persistent = browserStorage();
        if (!persistent) return false;
        persistent.removeItem(key);
        fallback.delete(key);
        return true;
      } catch {
        // swallow-ok: False explicitly reports failed persistent removal; session cache/reload-critical callers must not rely on removal succeeding, while optional mirrors are nonauthoritative.
        return false;
      }
    },
    clearExcept(keep: K): boolean {
      const keepValue = get(keep);
      try {
        const persistent = browserStorage();
        if (!persistent) return false;
        persistent.clear();
        if (keepValue !== null) persistent.setItem(keep, keepValue);
        fallback.clear();
        ignorePersistent = false;
        return true;
      } catch {
        // Callers must not reload when the persistent clear is incomplete.
        fallback.clear();
        if (keepValue !== null) fallback.set(keep, keepValue);
        ignorePersistent = true;
        return false;
      }
    },
  };
}

/** localStorage: shared by every tab of this origin. */
export const storage = createTypedStorage<StorageKey>(
  () => window.localStorage
);

/**
 * sessionStorage: scoped to this tab and kept across reloads. The in-memory
 * fallback lasts only for this page, so a value that must survive a reload
 * (such as a reload loop guard) uses setPersisted and checks its result.
 */
export const sessionStore = createTypedStorage<SessionStorageKey>(
  () => window.sessionStorage
);
